import { AnchorProvider, Program, Idl } from '@coral-xyz/anchor'
import { Connection, PublicKey, Keypair } from '@solana/web3.js'
import { WalletContextState } from '@solana/wallet-adapter-react'
import crypto from 'crypto'

import idl from './idl/junk_fun_program.json'

import config from '@/config'

// Get program ID from config
export const PROGRAM_ID = new PublicKey(config.SOLANA.PROGRAM.JUNK_FUN_PROGRAM_ID)

// Log the program ID being used (helps with debugging)
console.log('[ANCHOR-CLIENT] Initialized with Program ID:', PROGRAM_ID.toString())
console.log('[ANCHOR-CLIENT] Config JUNK_FUN_PROGRAM_ID:', config.SOLANA.PROGRAM.JUNK_FUN_PROGRAM_ID)
console.log('[ANCHOR-CLIENT] Config SOLANA_RPC_URL:', config.SOLANA_RPC_URL)

// Use the IDL directly as the program type
export type JunkFunProgram = Program<Idl>

/**
 * Get Solana connection with proper configuration
 */
export function getConnection(): Connection {
  const rpcUrl = config.SOLANA_RPC_URL || 'https://api.devnet.solana.com'

  return new Connection(rpcUrl, {
    commitment: 'confirmed',
    confirmTransactionInitialTimeout: 60000,
  })
}

/**
 * Create Anchor-compatible wallet adapter
 */
function createAnchorWallet(wallet: WalletContextState) {
  if (!wallet.publicKey || !wallet.signTransaction || !wallet.signAllTransactions) {
    throw new Error('Wallet not properly connected')
  }

  return {
    publicKey: wallet.publicKey,
    signTransaction: wallet.signTransaction.bind(wallet),
    signAllTransactions: wallet.signAllTransactions.bind(wallet),
  }
}

/**
 * Create Anchor provider from wallet
 */
export function getProvider(wallet: WalletContextState, connection?: Connection): AnchorProvider {
  const conn = connection || getConnection()
  const anchorWallet = createAnchorWallet(wallet)

  return new AnchorProvider(
    conn,
    anchorWallet as any,
    { commitment: 'confirmed' }
  )
}

/**
 * Get the Anchor program instance
 */
export function getProgram(provider: AnchorProvider): JunkFunProgram {
  // Anchor v0.31 uses the new IDL format, pass it directly
  return new Program(idl as Idl, provider)
}

/**
 * Get program with connection only (read-only)
 */
export function getProgramReadOnly(connection?: Connection): JunkFunProgram {
  const conn = connection || getConnection()
  const mockWallet = {
    publicKey: Keypair.generate().publicKey,
    signTransaction: async () => { throw new Error('Read-only mode') },
    signAllTransactions: async () => { throw new Error('Read-only mode') }
  }

  const provider = new AnchorProvider(conn, mockWallet as any, { commitment: 'confirmed' })
  return new Program(idl as Idl, provider)
}

/**
 * Derive PDA for GlobalConfig
 */
export function getGlobalConfigPDA(): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('global_config')],
    PROGRAM_ID
  )
}

/**
 * Derive PDA for UserInfo (main program - burning/chests)
 */
export function getUserInfoPDA(userPublicKey: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('user_info'), userPublicKey.toBuffer()],
    PROGRAM_ID
  )
}

/**
 * Derive PDA for UserInfo on the Swap program (dust swapping)
 * Now uses main program as swap operations have been consolidated
 */
export function getSwapUserInfoPDA(userPublicKey: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('user_info'), userPublicKey.toBuffer()],
    PROGRAM_ID
  )
}

/**
 * Derive PDA for PrizePool
 */
export function getPrizePoolPDA(): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('prize_pool')],
    PROGRAM_ID
  )
}

/**
 * Derive PDA for Event Authority
 */
export function getEventAuthorityPDA(): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('__event_authority')],
    PROGRAM_ID
  )
}

/**
 * Derive PDA for BurnOrder
 * Uses SHA-256 hash of orderId to generate seed
 *
 * @param orderId - Unique order identifier (typically UUID from backend)
 */
export function getBurnOrderPDA(orderId: string): [PublicKey, number] {
  const hexString = crypto.createHash('sha256').update(orderId, 'utf-8').digest('hex')
  const burnOrderSeed = Uint8Array.from(Buffer.from(hexString, 'hex'))
  return PublicKey.findProgramAddressSync(
    [Buffer.from('burn_order'), burnOrderSeed],
    PROGRAM_ID
  )
}

/**
 * Derive PDA for OpenChestOrder
 * Uses SHA-256 hash of orderId to generate seed
 *
 * @param orderId - Unique order identifier (typically UUID from backend)
 */
export function getOpenChestOrderPDA(orderId: string): [PublicKey, number] {
  const hexString = crypto.createHash('sha256').update(orderId, 'utf-8').digest('hex')
  const openChestOrderSeed = Uint8Array.from(Buffer.from(hexString, 'hex'))
  return PublicKey.findProgramAddressSync(
    [Buffer.from('open_chest_order'), openChestOrderSeed],
    PROGRAM_ID
  )
}

/**
 * Fetch user's accumulated swap balance from the Swap program's UserInfo PDA
 * Returns the balance in lamports, or null if the account doesn't exist
 *
 * Account structure:
 * - 8 bytes: Anchor discriminator
 * - 8 bytes: balance (u64, little-endian)
 * - 1 byte: bump (u8)
 */
export async function getSwapUserBalance(userPublicKey: PublicKey): Promise<number | null> {
  const connection = getConnection()
  const [swapUserInfoPDA] = getSwapUserInfoPDA(userPublicKey)

  try {
    const accountInfo = await connection.getAccountInfo(swapUserInfoPDA)
    if (!accountInfo || !accountInfo.data) {
      return null // Account doesn't exist yet
    }

    // Skip 8-byte discriminator, read 8-byte u64 balance (little-endian)
    const dataView = new DataView(accountInfo.data.buffer, accountInfo.data.byteOffset)
    const balance = dataView.getBigUint64(8, true) // offset 8, little-endian

    return Number(balance)
  } catch (error) {
    console.error('[ANCHOR-CLIENT] Error fetching swap user balance:', error)
    return null
  }
}